// #2121-spec-kits
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from "react-dom";
import moment from 'moment';
import {
  Card,
  CardBody,
  Form,
  Input,
  Button,
  Row,
  Col,
  Badge,
  UncontrolledTooltip,
  Label
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { translate } from 'react-translate';
import {
  actionUpdateKitStatus,
  actionGetKitTypes,
  actionSetEditKit,
  actionGetKit,
  actionGetKitStateMachine,
  actionGetKitStateGroups,
  actionSubmitObservations,
  actionSaveKitTracking
} from 'store/actions/kitsActions';
import { 
  actionGetAssetTypes,
} from 'store/actions/assetsActions';
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import { toast } from 'react-toastify';
import PrintableTemplate from './templates/PrintableTemplate';

// ---------- mappings ----------

// Color por estado (claves Reactstrap Badge / Bootstrap)
const STATUS_COLOR = {
  k_st_1:           'secondary',
  k_st_disassembled:         'secondary',
  k_st_2:                'success',
  k_st_reserved:             'info',
  k_st_3: 'info',
  k_st_4:  'primary',
  'k_st_5':      'warning',
  k_st_9:       'warning',
  k_st_6:             'danger',
  k_st_8:               'danger',
  k_st_10:              'warning',
  k_st_11:                  'dark',
  k_st_7:              'secondary',
};

// #2121 - El mapeo estado -> grupo (5 fases) vive en el backend
// (MS-Management/src/models/KitsModel.ts) y lo levantamos via
// /kits/state-groups -> redux.kitsReducer.stateGroups. Asi cualquier cambio
// del catalogo de grupos lo refleja el frontend sin tocar codigo.

// Icono now-ui-icons por tipo de asset
const ASSET_ICON = {
  cellphone:  'now-ui-icons tech_mobile',
  device:     'now-ui-icons objects_diamond',
  'sim card': 'now-ui-icons tech_tv',
};

const safeJsonParse = (s) => {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
};

// El log de kit guarda los estados en forma DESCRIPTIVA (k_st_assigned_to_practice)
// pero la i18n de kits usa la forma NUMÉRICA (k_st_3). Normalizamos antes de t().
const STATE_NORM = {
  k_st_assembling: 'k_st_1', k_st_1: 'k_st_1',
  k_st_ready: 'k_st_2', k_st_2: 'k_st_2',
  k_st_assigned_to_practice: 'k_st_3', k_st_3: 'k_st_3',
  k_st_assigned_to_patient: 'k_st_4', k_st_4: 'k_st_4',
  'k_st_in-production': 'k_st_5', k_st_5: 'k_st_5',
  k_st_returned: 'k_st_6', k_st_6: 'k_st_6',
  k_st_deleted: 'k_st_7', k_st_7: 'k_st_7',
  k_st_cancel: 'k_st_8', k_st_8: 'k_st_8',
  k_st_waiting_report: 'k_st_9', k_st_9: 'k_st_9',
  k_st_reclaim: 'k_st_10', k_st_10: 'k_st_10',
  k_st_end: 'k_st_11', k_st_11: 'k_st_11',
};
const normState = (s) => (s ? (STATE_NORM[s] || s) : s);

// Eventos crudos del backend → claves i18n amigables.
const EVENT_KEY = {
  'Kit status changed': 'ev-status-changed',
  'Observations updated': 'ev-observations',
  'Asset assigned to the kit': 'ev-asset-assigned',
  'Asset removed from the kit': 'ev-asset-removed',
  'Kit created (seed)': 'ev-created',
};

// El backend audita cambios de campos como pares old_<campo>/new_<campo>
// (KitsModel: observaciones, etc.). Mapeamos el campo a su clave i18n.
const FIELD_LABEL_KEY = {
  observation: 'observations',
};
const truncate = (v, n = 80) => {
  const s = String(v == null ? '' : v);
  return s.length > n ? `${s.slice(0, n)}…` : s;
};
// Extrae cambios auditados del log (excluye status: se muestra como transición).
const extractFieldChanges = (log, t) => {
  const changes = [];
  Object.keys(log).forEach((k) => {
    if (!k.startsWith('new_')) return;
    const field = k.slice(4);
    if (field === 'status') return; // cubierto por la transición de estado
    const labelKey = FIELD_LABEL_KEY[field] || field;
    changes.push({ field, label: t(labelKey), old: log[`old_${field}`], new: log[k] });
  });
  return changes;
};

// ---------- subcomponentes ----------

const InfoCard = ({ icon, title, children, empty, emptyText }) => (
  <Card className="kit-detail-card h-100">
    <CardBody>
      <div className="kit-detail-card-header">
        <i className={icon} />
        <span>{title}</span>
      </div>
      {empty
        ? <div className="kit-detail-empty">{emptyText}</div>
        : <div className="kit-detail-card-body">{children}</div>}
    </CardBody>
  </Card>
);

const Field = ({ label, value, copyable, onCopy }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="kit-detail-field">
      <span className="kit-detail-field-label">{label}</span>
      <span className="kit-detail-field-value">
        {value}
        {copyable && (
          <button
            type="button"
            className="kit-detail-copy-btn"
            onClick={onCopy}
            aria-label="Copy"
          >
            <i className="now-ui-icons files_single-copy-04" />
          </button>
        )}
      </span>
    </div>
  );
};

// #2121 - Stepper de 5 fases. El catalogo de grupos y el mapeo estado -> grupo
// vienen del backend. Mostramos el grupo activo en la barra y el label exacto
// del estado canonico debajo del paso activo para que el usuario vea ambos
// niveles de detalle.
const LifecycleBar = ({ currentStatus, stateGroups, t }) => {
  const groups       = (stateGroups && stateGroups.groups) || [];
  const stateToGroup = (stateGroups && stateGroups.stateToGroup) || {};

  // Si el backend todavia no contesto, no pintamos nada (evita flicker).
  if (groups.length === 0) return null;

  const activeGroup = stateToGroup[currentStatus];
  const stage = activeGroup ? groups.indexOf(activeGroup) : -1;

  return (
    <div className="kit-lifecycle">
      {groups.map((groupKey, idx) => {
        const reached = stage >= 0 && idx <= stage;
        const current = idx === stage;
        return (
          <React.Fragment key={groupKey}>
            <div className={`kit-lifecycle-step ${reached ? 'reached' : ''} ${current ? 'current' : ''}`}>
              <div className="kit-lifecycle-dot">
                {reached ? <i className="now-ui-icons ui-1_check" /> : <span>{idx + 1}</span>}
              </div>
              <div className="kit-lifecycle-label">{t(groupKey)}</div>
              {current && currentStatus && (
                <div className="kit-lifecycle-detail">{t(currentStatus)}</div>
              )}
            </div>
            {idx < groups.length - 1 && (
              <div className={`kit-lifecycle-connector ${reached && idx < stage ? 'reached' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ActivityTimeline = ({ tracking, t }) => {
  if (!tracking || !tracking.length) {
    return <div className="kit-detail-empty">{t('no-activity')}</div>;
  }
  // ordenamos descendiendo (más reciente arriba)
  const items = [...tracking].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return (
    <ul className="kit-timeline kit-timeline--scroll">
      {items.map((entry, i) => {
        const log = safeJsonParse(entry.log) || {};
        const evKey = log.event && EVENT_KEY[log.event];
        const headline = entry.statusId
          ? t(normState(entry.statusId))
          : (evKey ? t(evKey) : (log.event || t('ev-generic')));
        // Transición de estado legible (estados traducidos, no códigos crudos).
        const fromS = log.old_status || log.oldStatus;
        const toS = log.new_status || log.status || entry.statusId;
        const showTransition = !!fromS && !!toS;
        const tone = toS ? (STATUS_COLOR[normState(toS)] || 'info') : 'secondary';
        // Usuario combinado, sin volcar keys técnicas.
        const userName = [log.user_first_name, log.user_last_name].filter(Boolean).join(' ').trim();
        // Cambios de campos auditados (observaciones, etc.).
        const fieldChanges = extractFieldChanges(log, t);
        return (
          <li key={`${entry._id || i}`} className={`kit-timeline-item tone-${tone}`}>
            <div className="kit-timeline-dot" />
            <div className="kit-timeline-card">
              <div className="kit-timeline-meta">
                <strong>{headline}</strong>
                <time>{moment(entry.date).format('DD-MM-YYYY HH:mm:ss')}</time>
              </div>
              {showTransition && (
                <div className="kit-timeline-transition">
                  <span className="kit-tl-chip">{t(normState(fromS))}</span>
                  <i className="now-ui-icons arrows-1_minimal-right" aria-hidden="true" />
                  <span className="kit-tl-chip kit-tl-chip--to">{t(normState(toS))}</span>
                </div>
              )}
              {fieldChanges.length > 0 && (
                <div className="kit-timeline-changes">
                  {fieldChanges.map((c) => (
                    <div className="kit-tl-change" key={c.field}>
                      <span className="kit-tl-field">{c.label}</span>
                      <span className="kit-tl-chip">{truncate(c.old) || '—'}</span>
                      <i className="now-ui-icons arrows-1_minimal-right" aria-hidden="true" />
                      <span className="kit-tl-chip kit-tl-chip--to">{truncate(c.new) || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {userName && (
                <div className="kit-timeline-user">
                  {t('activity-by')} <span>{userName}</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const AssetCard = ({ asset }) => {
  const icon = ASSET_ICON[asset.asset_type] || 'now-ui-icons objects_diamond';
  return (
    <div className="kit-asset-card">
      <i className={`kit-asset-icon ${icon}`} />
      <div className="kit-asset-info">
        <div className="kit-asset-type">{asset.asset_type || '—'}</div>
        {asset.brand   && <div className="kit-asset-row"><span>Brand</span>  <span>{asset.brand}</span></div>}
        {asset.model   && <div className="kit-asset-row"><span>Model</span>  <span>{asset.model}</span></div>}
        {asset.serial_number && <div className="kit-asset-row"><span>Serial</span> <span>{asset.serial_number}</span></div>}
        {asset._id     && <div className="kit-asset-id">{asset._id}</div>}
      </div>
    </div>
  );
};

// ---------- main ----------

const PatientKitDetails = (props) => {
  const dispatch = useDispatch();
  const { t } = props;
  const { id } = useParams();
  const history = useHistory();
  const iframeRef = useRef(null);

  const { kitEdit } = useSelector((state) => state.kitsReducer);
  const kitTypes = useSelector((state) => state.kitsReducer.types);
  const stateMachine = useSelector((state) => state.kitsReducer.stateMachine);
  const stateGroups  = useSelector((state) => state.kitsReducer.stateGroups);
  const assetTypes = useSelector((s) => s.assetsReducer.types);

  const observationsRef = useRef(null);
  const [obsDirty, setObsDirty] = useState(false);
  const [obsSaving, setObsSaving] = useState(false);
  const [trackingNum, setTrackingNum] = useState("");
  const { register, handleSubmit, watch } = useForm();

  // selected status (preview de transición)
  const watchStatus = watch('status');

  useEffect(() => {
    if (!Object.keys(kitEdit).length && id) dispatch(actionGetKit(id));
    if (!Object.keys(kitTypes).length)     dispatch(actionGetKitTypes());
    if (!Object.keys(stateMachine).length) dispatch(actionGetKitStateMachine());
    if (!Object.keys(assetTypes).length) dispatch(actionGetAssetTypes());
    if (!stateGroups || !stateGroups.groups || stateGroups.groups.length === 0) {
      dispatch(actionGetKitStateGroups());
    }
    return () => { dispatch(actionSetEditKit({})); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTrackingNum(kitEdit?.delivery_info?.tracking?.tracking_id || "");
  }, [kitEdit]);

  // address parsing tolerante (puede venir como JSON o como string libre)
  const address = useMemo(() => {
    const raw = kitEdit?.delivery_info?.delivery_address;
    if (!raw) return null;
    const parsed = safeJsonParse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
    return { address: String(raw) };
  }, [kitEdit]);

  const practiceInfo = useMemo(() => {
    if (kitEdit?.delivery_info?.practice) return kitEdit.delivery_info.practice;
    if (kitEdit?.practice_data) return kitEdit.practice_data;
    return null;
  }, [kitEdit]);

  const onSubmit = (data) => {
    if (!data.status || data.status === kitEdit.status) return;
    dispatch(actionUpdateKitStatus(kitEdit._id, data.status));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label || t('id-copied'));
  };

  const copyAddress = () => {
    if (!address) return;
    const text = Object.entries(address).map(([k, v]) => `${k}: ${v}`).join('\n');
    copyToClipboard(text, t('delivery-copied'));
  };

  const submitObservations = (e) => {
    if (e) e.preventDefault();
    if (!observationsRef.current) return;
    setObsSaving(true);
    dispatch(actionSubmitObservations(kitEdit._id, observationsRef.current.value));
    setObsDirty(false);
    setTimeout(() => setObsSaving(false), 600);
  };

  const handleHTMLPrint = (e) => {
    const iframe = iframeRef.current;

    dispatch(actionSaveKitTracking(kitEdit._id, trackingNum));

    if (!iframe) return;

    const iframeDocument =
      iframe.contentDocument || iframe.contentWindow.document;

    //Prepare base HTML document
    iframeDocument.open();
    iframeDocument.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Lieferschein - Dexcom Telediagnostik (Beispiel, S/W)</title>
        </head>
        <body>
          <div id="print-root"></div>
        </body>
      </html>
    `);
    iframeDocument.close();

    //Render the component
    ReactDOM.render(<PrintableTemplate 
      kit={kitEdit} 
      assetTypes={assetTypes} 
      kitTypes={kitTypes}
      trackingNumber={trackingNum}
    />, iframeDocument.getElementById("print-root"));

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 250);
  }

  // ---------- render ----------

  if (!Object.keys(kitEdit).length) {
    return <div className="content user-manual-edit mt-3"><LoadingLocal /></div>;
  }

  const status = kitEdit.status;
  const statusColor = STATUS_COLOR[status] || 'secondary';
  const transitions = stateMachine[status] || [];
  const refId = `KITID${kitEdit.friendly_id}KITID`;
  const bookingId = kitEdit?.delivery_info?.booking?.booking_id;
  const hasDelivery = !!kitEdit.delivery_info;
  const patient = kitEdit?.delivery_info?.patient;
  const doctor  = kitEdit?.delivery_info?.doctor;

  return (
    <div className="content kit-detail-page mt-3">

      {/* Breadcrumb */}
      <div className="kit-detail-breadcrumb">
        <button
          type="button"
          className="kit-detail-back"
          onClick={(e) => { e.preventDefault(); history.goBack(); }}
        >
          <i className="now-ui-icons arrows-1_minimal-left" />
          <span>{t('back-to-list')}</span>
        </button>
        <span className="kit-detail-crumb">{t('title-header')}</span>
        <i className="now-ui-icons arrows-1_minimal-right kit-detail-crumb-sep" />
        <span className="kit-detail-crumb-current">{kitEdit.friendly_id}</span>
      </div>

      {/* HERO */}
      <Card className={`kit-hero status-${statusColor}`}>
        <CardBody>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="kit-hero-flex">
                <div className="kit-hero-icon">
                  <i className="now-ui-icons shopping_box" />
                </div>
                <div>
                  <div className="kit-hero-friendly">{kitEdit.friendly_id}</div>
                  <div className="kit-hero-row">
                    <span className="kit-hero-label">{t('kit-id-label') || 'Kit ID'}</span>
                    <code>{kitEdit._id}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={() => copyToClipboard(kitEdit._id)}
                      id="copy-kit-id"
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                    <UncontrolledTooltip target="copy-kit-id" placement="top">{t('copy-btn')}</UncontrolledTooltip>
                  </div>
                  <div className="kit-hero-row">
                    <span className="kit-hero-label">{t('ref-id-label') || 'Ref ID'}</span>
                    <code>{refId}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={() => copyToClipboard(refId)}
                      id="copy-ref-id"
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                    <UncontrolledTooltip target="copy-ref-id" placement="top">{t('copy-btn')}</UncontrolledTooltip>
                  </div>

                  {bookingId ? (
                    <div className="kit-hero-row">
                      <span className="kit-hero-label">{t('booking-id-label') || 'Booking ID'}</span>
                      <code>{bookingId}</code>
                      <button
                        type="button"
                        className="kit-detail-copy-btn"
                        onClick={() => copyToClipboard(bookingId)}
                        id="copy-booking-id"
                        aria-label="Copy"
                      >
                        <i className="now-ui-icons files_single-copy-04" />
                      </button>
                      <UncontrolledTooltip target="copy-booking-id" placement="top">{t('copy-btn')}</UncontrolledTooltip>
                    </div>
                  ): <></>}

                  <div className="kit-hero-meta">
                    <span><i className="now-ui-icons design_app" /> {kitEdit.type || '—'}</span>
                    <span><i className="now-ui-icons ui-1_calendar-60" /> {moment(kitEdit.created_at).format('DD-MM-YYYY HH:mm:ss')}</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-md-right text-left mt-3 mt-md-0">
              <Badge color={statusColor} className="kit-hero-badge" pill>
                {t(status)}
              </Badge>

              {kitEdit.status_date && (
                <div className="kit-hero-status-date">
                  {t('status')} · {moment(kitEdit.status_date).fromNow()}
                </div>
              )}
            </Col>
          </Row>

          <hr className="kit-hero-sep" />

          {/* Hidden iframe to display any HTML file and print it */}
          <iframe
            ref={iframeRef}
            style={{display: "none"}}
          />

          <LifecycleBar currentStatus={status} stateGroups={stateGroups} t={t} />
        </CardBody>
      </Card>

      {/* Invoice print section */}
      {bookingId ? (
        <Card className="kit-detail-card kit-status-control">
          <CardBody>
            <div className="kit-detail-card-header">
              <i className="now-ui-icons ui-2_settings-90" />
              <span>{t('delivery-note')}</span>
            </div>

            <div className="kit-status-control-row">
              <div className="d-flex align-items-center">
                <span className="kit-status-control-tag">{t('tracking-number')}</span>
                <Input 
                  type="text" 
                  name="tracking_number" 
                  id="tracking_number" 
                  className="ml-1"
                  onChange={(e) => { setTrackingNum(e.target.value) }} 
                  defaultValue={kitEdit?.delivery_info?.tracking?.tracking_id || ''}
                />

                <Button className="ml-3" disabled={!trackingNum.length} size="sm" color="info" onClick={(e) => {handleHTMLPrint(e)}}>
                  {t('generate-delivery-note')}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (<></>)}

      {/* INFO GRID */}
      <Row className="mb-3">
        <Col lg={6}>
          <InfoCard
            icon="now-ui-icons business_briefcase-24"
            title={t('practice')}
            empty={!practiceInfo}
            emptyText={t('no-practice-yet')}
          >
            <Field label={t('name')}      value={practiceInfo?.name || practiceInfo?.practice_name} />
            {practiceInfo?.owner && (
              <Field
                label={t('doctor')}
                value={`${practiceInfo.owner.firstName} ${practiceInfo.owner.lastName}`}
              />
            )}
            {practiceInfo?.practice_id && (
              <Field
                label="ID"
                value={practiceInfo.practice_id}
                copyable
                onCopy={() => copyToClipboard(practiceInfo.practice_id)}
              />
            )}
          </InfoCard>
        </Col>
        <Col lg={6}>
          <InfoCard
            icon="now-ui-icons users_circle-08"
            title={t('doctor')}
            empty={!doctor || !Object.keys(doctor || {}).length}
            emptyText={t('no-doctor-yet')}
          >
            <Field label={t('full-name')} value={doctor && `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim()} />
            <Field
              label={t('tp-id')}
              value={doctor?.auth_user_id}
              copyable
              onCopy={() => copyToClipboard(String(doctor?.auth_user_id || ''))}
            />
          </InfoCard>
        </Col>
      </Row>

      <br/>
      

      

      {/* Status Control */}
      <Card className="kit-detail-card kit-status-control">
        <CardBody>
          <div className="kit-detail-card-header">
            <i className="now-ui-icons ui-2_settings-90" />
            <span>{t('current-state') || 'Status'}</span>
          </div>
          <Form id="kit-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="kit-status-control-row">
              <div className="kit-status-control-current">
                <span className="kit-status-control-tag">{t('current-state') || 'Current'}</span>
                <Badge color={statusColor} pill>{t(status)}</Badge>
              </div>
              <i className="now-ui-icons arrows-1_minimal-right kit-status-arrow" />
              <div className="kit-status-control-target">
                <span className="kit-status-control-tag">{t('next-state') || 'Next'}</span>
                <Input
                  type="select"
                  name="status"
                  id="status"
                  defaultValue={status}
                  innerRef={register}
                  className="kit-status-select"
                >
                  {transitions.length
                    ? transitions.map((st) => (<option key={st} value={st}>{t(st)}</option>))
                    : <option value={status}>{t('no-state-changes') || '—'}</option>}
                </Input>
              </div>
              <Button
                size="sm"
                color="primary"
                type="submit"
                form="kit-form"
                disabled={!watchStatus || watchStatus === status || transitions.length === 0}
                className="kit-status-save"
              >
                <i className="now-ui-icons ui-1_check" /> {t('change-state-btn') || t('save-btn')}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* PATIENT + ADDRESS (solo si delivery_info) */}
      {hasDelivery && (
        <Row className="mb-3">
          <Col lg={7}>
            <InfoCard
              icon="now-ui-icons users_single-02"
              title={t('patient-info')}
              empty={!patient || !Object.keys(patient || {}).length}
              emptyText={t('no-patient-yet')}
            >
              <Field
                label={t('full-name')}
                value={patient && `${patient.firstName || ''} ${patient.lastName || ''}`.trim()}
              />
              <Field
                label={t('tp-id')}
                value={patient?.auth_user_id}
                copyable
                onCopy={() => copyToClipboard(String(patient?.auth_user_id || ''))}
              />
              <Field label={t('ext-ref')}      value={patient?.ext_ref} />
              <Field label={t('phone-number')} value={patient?.phone} />
            </InfoCard>
          </Col>
          <Col lg={5}>
            <InfoCard
              icon="now-ui-icons location_pin"
              title={t('delivery-address')}
              empty={!address}
              emptyText={t('no-address-yet')}
            >
              {address && Object.entries(address).map(([k, v]) => (
                <div className="kit-detail-field" key={k}>
                  <span className="kit-detail-field-label">{k}</span>
                  <span className="kit-detail-field-value">{v}</span>
                </div>
              ))}
              {address && (
                <button
                  type="button"
                  className="btn btn-sm btn-link kit-address-copy"
                  onClick={copyAddress}
                >
                  <i className="now-ui-icons files_single-copy-04" /> {t('copy-btn')}
                </button>
              )}
            </InfoCard>
          </Col>
        </Row>
      )}

      {/* ASSETS */}
      <Card className="kit-detail-card">
        <CardBody>
          <div className="kit-detail-card-header">
            <i className="now-ui-icons design_bullet-list-67" />
            <span>{t('reserved-assets')}</span>
            <Badge color="light" className="kit-detail-count" pill>{kitEdit.assets?.length || 0}</Badge>
          </div>
          {kitEdit.assets && kitEdit.assets.length ? (
            <div className="kit-asset-grid">
              {kitEdit.assets.map((as) => (
                <AssetCard key={as._id || String(as)} asset={typeof as === 'object' ? as : { _id: as }} />
              ))}
            </div>
          ) : (
            <div className="kit-detail-empty">{t('no-assets')}</div>
          )}
        </CardBody>
      </Card>

      {/* TIMELINE + OBSERVATIONS */}
      <Row className="mb-3">
        <Col lg={7}>
          <Card className="kit-detail-card">
            <CardBody>
              <div className="kit-detail-card-header">
                <i className="now-ui-icons ui-2_time-alarm" />
                <span>{t('activity-log') || t('kit-tracking-info')}</span>
              </div>
              <ActivityTimeline tracking={kitEdit.statesTracking} t={t} />
            </CardBody>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="kit-detail-card h-100">
            <CardBody className="d-flex flex-column">
              <div className="kit-detail-card-header">
                <i className="now-ui-icons ui-1_simple-add" />
                <span>{t('observations')}</span>
                {obsDirty && <span className="kit-obs-dirty">●</span>}
              </div>
              <Input
                innerRef={observationsRef}
                type="textarea"
                id="observations"
                name="observations"
                defaultValue={kitEdit.observations || ''}
                onChange={() => setObsDirty(true)}
                className="kit-obs-textarea flex-grow-1"
                placeholder={t('observations-placeholder') || ''}
              />
              <div className="kit-obs-footer">
                <Button
                  size="sm"
                  color={obsDirty ? 'primary' : 'secondary'}
                  onClick={submitObservations}
                  disabled={!obsDirty || obsSaving}
                >
                  {obsSaving
                    ? <><i className="now-ui-icons ui-2_settings-90 spin" /> ...</>
                    : <><i className="now-ui-icons ui-1_check" /> {t('save-observations') || t('save-btn')}</>}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

    </div>
  );
};

export default translate('kits')(PatientKitDetails);
